import { get, post } from 'helpers/api';
import { setLoading } from './ui';
import { isNil } from 'lodash';
import { bookmarkedProperties, fetchPropertiesByInsightsId } from 'helpers/clarity_api';

import debounceActionCreators from 'helpers/redux/debounce_action_creators';
const debouncedActionCreators = debounceActionCreators({ search }, 500);

export const debouncedSearch = debouncedActionCreators.search;
export const SET_RESULTS = 'SET_RESULTS';
const OFF_MARKET_DETAILED_RESULTS_LIMIT = 20;
const OFF_MARKET_MAP_RESULTS_LIMIT = 500;


async function criteriaToOffMarketSearchParams(criteria) {
  const params = {
    ...criteria,
    mapBounds: criteria.mapBounds || []
  };

  if (params.bookmarked) {
    const properties = await bookmarkedProperties(params.bookmarked);

    params.propertyIds = (properties || []).map(property => property.external_id).filter(id => !isNil(id));
  }

  delete params.bookmarked;

  return params;
}

export function paginate(page = 1) {
  return async (dispatch, getState) => {
    dispatch(setLoading(true));
    const { criteria, ui } = getState();
    if (ui.offMarketSearch) {
      const params = await criteriaToOffMarketSearchParams(criteria);
      params.limit = OFF_MARKET_DETAILED_RESULTS_LIMIT;
      params.offset = OFF_MARKET_DETAILED_RESULTS_LIMIT * (page - 1);

      post('/connect/off_market_properties/search', { insightsProperty: params }).then((detailedResults) => {
        dispatch(setLoading(false));
        dispatch({
          type: SET_RESULTS,
          payload: {
            offMarketResults: detailedResults.hits || [],
            page: detailedResults.page,
            total: detailedResults.total,
            totalPages: detailedResults.totalPages
          }
        });
      });
    } else {
      post('/connect/search/search', { set: criteria, page }).then((detailedResults) => {
        dispatch(setLoading(false));
        dispatch({
          type: SET_RESULTS,
          payload: {
            detailedResults: detailedResults.results,
            page: detailedResults.page,
            total: detailedResults.total,
            totalPages: detailedResults.totalPages
          }
        });
      });
    }
  };
}

export function search() {
  return async (dispatch, getState) => {
    dispatch(setLoading(true));
    const { criteria, ui } = getState();
    if (ui.offMarketSearch) {
      const params = await criteriaToOffMarketSearchParams(criteria);
      params.includeGeometry = ui.mapZoomReadonly && ui.mapZoomReadonly >= 15;

      const allResultsRequest = post('/connect/off_market_properties/search_all',
        { insightsProperty: { ...params, limit: OFF_MARKET_MAP_RESULTS_LIMIT } });

      const detailedResultsRequest = post('/connect/off_market_properties/search',
        { insightsProperty: { ...params, limit: OFF_MARKET_DETAILED_RESULTS_LIMIT } });

      const [allResults, detailedResults] = await Promise.all([allResultsRequest, detailedResultsRequest]);

      if (ui.products.prospecting && detailedResults.hits) {
        const insightsIds = detailedResults.hits.map(hit => hit.propertyId);
        const clarityProperties = await fetchPropertiesByInsightsId(insightsIds);

        clarityProperties.forEach((clarityProperty) => {
          const matchingProperty = detailedResults.hits.find(property => property.propertyId === clarityProperty.external_id);
          if (matchingProperty) {
            matchingProperty.favorited = clarityProperty.favorited;
          }
        });
      }

      dispatch(setLoading(false));
      dispatch({
        type: SET_RESULTS,
        payload: {
          allOffMarketResults: allResults.hits || [],
          totalOffMarketResults: allResults.total,
          offMarketResults: detailedResults.hits || [],
          page: 1,
          total: detailedResults.total,
          totalPages: detailedResults.totalPages
        }
      });
    } else {
      const allResultsRequest = post('/connect/search/search_all', { set: criteria });
      const detailedResultsRequest = post('/connect/search/search', { set: criteria });

      const [allResults, detailedResults] = await Promise.all([allResultsRequest, detailedResultsRequest]);

      dispatch(setLoading(false));
      dispatch({
        type: SET_RESULTS,
        payload: {
          allResults,
          detailedResults: detailedResults.results,
          page: 1,
          total: detailedResults.total,
          totalPages: detailedResults.totalPages
        }
      });
    }
  };
}

export function findListing(listingId) {
  return get('/connect/search/info', { params: { listingId } });
}

export function findOffMarketProperty(propertyId) {
  return get(`/connect/off_market_properties/${propertyId}`);
}

function saveCriteriaLocationsAttributes(criteria) {
  if (criteria.locationsAttributes && criteria.locationsAttributes.length > 0) return criteria.locationsAttributes;

  if (criteria.mapBounds) return [{
    name: 'Map View',
    polygonSearch: true,
    polygonGeojson: {
      type: 'Polygon',
      coordinates: [[
        [criteria.mapBounds.east, criteria.mapBounds.north],
        [criteria.mapBounds.west, criteria.mapBounds.north],
        [criteria.mapBounds.west, criteria.mapBounds.south],
        [criteria.mapBounds.east, criteria.mapBounds.south],
        [criteria.mapBounds.east, criteria.mapBounds.north],
      ]]
    }
  }];
}

export const SAVE_SEARCH = 'SAVE_SEARCH';
export function saveSearch(name) {
  return async (_dispatch, getState) => {
    const { criteria, ui } = { ...getState() };
    return post(
      '/connect/search/save_search',
      {
        set: {
          ...criteria,
          locationsAttributes: saveCriteriaLocationsAttributes(criteria),
          name: name ? name : undefined,
          type: ui.offMarketSearch ? 'Connect::OffMarketSavedSearch' : 'Connect::BuildoutSavedSearch'
        }
      }
    );
  };
}

// Sends the given criteria to the server to get the summary of the filters. The returned value is
// { filters: [{ label: String, icon: String }], name: String}
export function previewCriteria(criteria) {
  return post('/connect/saved_searches/filter_summary', { saved_search: criteria });
}
